import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/components/ui/sonner";
import { playTone, stopAll, playBinaural, pauseAll, resumeAll } from "@/lib/audioEngine";
import { Search, Square, Play, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
const MAX_TONES = 12;

const QUICK_FREQS = [
  { hz: 174, label: "174", tag: "Pain" },
  { hz: 285, label: "285", tag: "Tissue" },
  { hz: 396, label: "396", tag: "Fear" },
  { hz: 417, label: "417", tag: "Change" },
  { hz: 432, label: "432", tag: "Harmony" },
  { hz: 528, label: "528", tag: "DNA" },
  { hz: 639, label: "639", tag: "Connect" },
  { hz: 741, label: "741", tag: "Intuition" },
  { hz: 852, label: "852", tag: "Spirit" },
  { hz: 963, label: "963", tag: "Crown" },
];

const SOLFEGGIO_FULL = [174,285,396,417,432,528,639,741,852,963];
const CHAKRAS = [
  {name:"Root", hz:396}, {name:"Sacral", hz:417}, {name:"Solar Plexus", hz:528},
  {name:"Heart", hz:639}, {name:"Throat", hz:741}, {name:"Third Eye", hz:852}, {name:"Crown", hz:963},
];
const MOOD_PRESETS = {
  CALM: { carrier: 288, beat: 6 }, SAVAGE: { carrier: 864, beat: 14 }, "RAMPED UP": { carrier: 864, beat: 14 },
  "BI POLAR": { carrier: 432, beat: 10 }, NORMAL: { carrier: 432, beat: 10 }, HAPPY: { carrier: 324, beat: 10 },
  BLISS: { carrier: 528, beat: 10 }, EUPHORIA: { carrier: 432, beat: 40 },
};
const FREQ_PRESETS = [
  { id: "low-alpha", label: "Low Alpha", keywords: ["calm"], frequencies: [7.83, 10, 12] },
  { id: "mid-theta", label: "Mid Theta", keywords: ["focus"], frequencies: [4, 6, 7.83] },
  { id: "deep-delta", label: "Deep Delta", keywords: ["sleep"], frequencies: [1, 2, 3.5] },
  { id: "high-beta", label: "High Beta", keywords: ["energy"], frequencies: [20, 25, 30] },
  { id: "gamma-burst", label: "Gamma Burst", keywords: ["gamma"], frequencies: [40, 50, 100] },
  { id: "schumann", label: "Schumann Resonance", keywords: ["earth"], frequencies: [7.83, 14.1, 20.3] },
  { id: "solfeggio-stack", label: "Solfeggio Stack", keywords: ["solfeggio"], frequencies: [174, 285, 396, 417, 528, 639, 741, 852, 963] },
  { id: "full-spectrum", label: "Full Spectrum", keywords: ["allergy","sinus","rhinitis"], frequencies: [7.83, 160, 330, 727, 787, 880, 741, 528, 5000, 10000] },
];
const EEG_PRESETS = [
  { id: "alpha-reduce", name: "Alpha Reduce", carrier: 432, beat: 40, desc: "Alpha + gamma pattern" },
  { id: "theta-mod", name: "Theta Mod", carrier: 432, beat: 10, desc: "Alpha/theta pattern" },
  { id: "gamma-power", name: "Gamma Power", carrier: 432, beat: 25, desc: "High gamma pattern" },
  { id: "alpha-theta", name: "Alpha Theta", carrier: 432, beat: 8, desc: "Alpha/theta blend" },
  { id: "deep-alpha", name: "Deep Alpha", carrier: 288, beat: 6, desc: "Low alpha slowing" },
  { id: "wide-beta", name: "Wide Beta", carrier: 864, beat: 14, desc: "Beta stimulation" },
];
// Chakra mapping for tone layers — color + intent + glyph
// Frequency ranges follow Solfeggio + brainwave conventions.
const CHAKRA_MAP = [
  { range: [0, 30],      key: 'BRAINWAVE', color: '#4A90E2', intent: 'BRAINWAVE LAYER',     glyph: '⊕' },
  { range: [30, 200],    key: 'GAMMA',     color: '#E74C3C', intent: 'GAMMA / FOUNDATION',  glyph: '◉' },
  { range: [200, 401],   key: 'ROOT',      color: '#FF1A1A', intent: 'GROUNDING / STABILITY', glyph: '✦' },
  { range: [401, 425],   key: 'SACRAL',    color: '#FF8800', intent: 'CREATIVITY / FLOW',   glyph: '✦' },
  { range: [425, 530],   key: 'SOLAR',     color: '#FFD700', intent: 'POWER / CONFIDENCE',  glyph: '✦' },
  { range: [530, 640],   key: 'HEART',     color: '#00E676', intent: 'LOVE / HARMONY',      glyph: '✦' },
  { range: [640, 760],   key: 'THROAT',    color: '#40C4FF', intent: 'EXPRESSION / TRUTH',  glyph: '✦' },
  { range: [760, 870],   key: 'THIRD EYE', color: '#7C4DFF', intent: 'INTUITION / CLARITY', glyph: '✦' },
  { range: [870, 9999],  key: 'CROWN',     color: '#E040FB', intent: 'CONNECTION / UNITY',  glyph: '✦' },
];
const chakraFor = (hz) => CHAKRA_MAP.find(c => hz >= c.range[0] && hz < c.range[1]) || CHAKRA_MAP[CHAKRA_MAP.length - 1];

// Static-ish sparkline (deterministic per freq, animated by phase)
function Sparkline({ color, freq, gain }) {
  const points = [];
  const bars = 28;
  const seed = freq % 100;
  for (let i = 0; i < bars; i++) {
    const v = (Math.sin(i * 0.5 + seed * 0.1) * 0.4 + Math.cos(i * 0.3 + seed * 0.05) * 0.3 + 0.5);
    points.push(Math.max(0.1, Math.min(1, v * (0.3 + gain * 4))));
  }
  return (
    <div className="flex items-end gap-[2px] h-6 flex-1" data-testid="layer-sparkline">
      {points.map((v, i) => (
        <div key={i} className="flex-1" style={{
          height: `${v * 100}%`,
          background: `linear-gradient(to top, ${color}, ${color}88)`,
          minWidth: '2px',
          opacity: 0.85,
        }} />
      ))}
    </div>
  );
}

const POPULAR_BEATS = [7.83, 528, 728, 777, 813, 963, 999];

const COMBO_BANDS = [
  { id: 'delta', label: 'δ', name: 'DELTA', hz: 2, desc: 'Deep rest' },
  { id: 'theta', label: 'θ', name: 'THETA', hz: 6, desc: 'Meditation' },
  { id: 'alpha', label: 'α', name: 'ALPHA', hz: 10, desc: 'Calm focus' },
  { id: 'beta', label: 'β', name: 'BETA', hz: 14, desc: 'Active' },
  { id: 'gamma', label: 'γ', name: 'GAMMA', hz: 40, desc: 'Insight' },
];

// Prebuilt combo protocols — full tone-layer + binaural recipes (overrides combo state)
const PROTOCOLS = [
  {
    id: 'nac-shutoff',
    label: 'NAC SHUTOFF',
    title: 'NAC Craving Shutoff',
    description: '432 Hz foundation · 3.5 Hz delta binaural · 353/417 Hz layers',
    carrier: 432,
    beat: 3.5,
    binauralGain: 0.25,
    seconds: 1200,
    layers: [
      { freq: 432, gain: 0.10 },
      { freq: 353, gain: 0.08 },
      { freq: 417, gain: 0.04 },
      { freq: 40, gain: 0.03 }, // gamma presence layer
    ],
    download: '/api/uploads/audio/godmode_nac_craving_shutoff_432Hz.wav',
  },
];

export default function ToneGenerator() {
  const [rows, setRows] = useState([{ id: 1, freq: 432, gain: 0.2, pan: 0 }]);
  const [bi, setBi] = useState({ carrier: 432, beat: 8, gain: 0.3, leftAdjust: 0, rightAdjust: 0, minutes: 5 });
  const [presets, setPresets] = useState([]);
  const [rife, setRife] = useState([]);
  const [q, setQ] = useState("");
  const [loaded, setLoaded] = useState(false);
  const nodesRef = useRef([]);
  const binauralRef = useRef(null);

  // Expanded sections in archive
  const [expandedSections, setExpandedSections] = useState({});
  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const [currentMood, setCurrentMood] = useState(null);
  const [activeAnalog, setActiveAnalog] = useState(null);
  const [activeProtocol, setActiveProtocol] = useState(null);
  const protocolNodesRef = useRef([]);

  // ─── Combo (Tones + Binaural) state ───
  const [comboBand, setComboBand] = useState('alpha');
  const [comboCarrier, setComboCarrier] = useState(432);
  const [activeCombo, setActiveCombo] = useState(null);

  // Per-layer Solo/Mute state — keyed by row id
  const [layerMuted, setLayerMuted] = useState({});
  const [soloLayer, setSoloLayer] = useState(null);
  const toggleMute = (id) => setLayerMuted(m => ({ ...m, [id]: !m[id] }));
  const toggleSolo = (id) => setSoloLayer(s => s === id ? null : id); // label of currently active combo

  const handleMood = (label) => {
    const p = MOOD_PRESETS[label];
    if (!p) return;
    const calib = Number(localStorage.getItem("calibrationCents") || 0);
    stopAll();
    playBinaural({ carrierHz: p.carrier, beatHz: p.beat, seconds: 300, gain: 0.35, offsetCents: calib });
    setCurrentMood(label);
    toast.success(`Playing ${label}`);
  };

  const playAnalog = (analog) => {
    const calib = Number(localStorage.getItem("calibrationCents") || 0);
    stopAll();
    setActiveAnalog(analog.id);
    playBinaural({ carrierHz: analog.carrier, beatHz: analog.beat, seconds: 600, gain: 0.35, offsetCents: calib });
    toast.success(`Playing ${analog.name}`);
  };

  const stopAnalog = () => { stopAll(); setActiveAnalog(null); };

  const getProtocolFrequencies = (keywords = []) => {
    const match = rife.find((entry) => keywords.some((kw) => (entry.condition || "").toLowerCase().includes(kw)));
    return match?.frequencies || [];
  };

  const stopProtocolStack = () => {
    protocolNodesRef.current.forEach((node) => { try { node.stop?.(); } catch {} });
    protocolNodesRef.current = [];
    setActiveProtocol(null);
  };

  const playProtocolStack = (protocolId, frequencies = []) => {
    if (!frequencies.length) return;
    if (activeProtocol === protocolId) { stopProtocolStack(); return; }
    stopAll(); stopProtocolStack();
    const calib = Number(localStorage.getItem("calibrationCents") || 0);
    const nodes = frequencies.slice(0, 10).map((freq) => playTone({ freq, gain: 0.12, offsetCents: calib }));
    protocolNodesRef.current = nodes;
    setActiveProtocol(protocolId);
  };

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      axios.get(`${API}/frequencies`).then(r => setPresets(r.data)).catch(() => {});
      axios.get(`${API}/rife`).then(r => setRife(r.data)).catch(() => {});
    }
  }, [loaded]);

  const addRow = () => {
    if (rows.length >= MAX_TONES) { toast.error(`Max ${MAX_TONES} layers`); return; }
    const nr = { id: Date.now(), freq: 432, gain: 0.15, pan: 0 };
    setRows(p => { const n = [...p, nr]; if (nodesRef.current.length) nodesRef.current.push(playTone({ freq: nr.freq, gain: nr.gain, pan: nr.pan })); return n; });
  };

  const update = (i, key, val) => {
    setRows(p => {
      const c = p.slice(); c[i] = { ...c[i], [key]: val };
      const node = nodesRef.current[i];
      if (node) { const now = node.ctx?.currentTime || 0; if (key === 'freq') node.osc.frequency.setValueAtTime(Number(val) || 0, now); if (key === 'gain') node.gainNode?.gain?.setValueAtTime(Number(val) || 0, now); if (key === 'pan' && node.panner) node.panner.pan.setValueAtTime(Number(val) || 0, now); }
      return c;
    });
  };

  const removeRow = (idx) => {
    setRows(p => { const n = p.filter((_, i) => i !== idx); const node = nodesRef.current[idx]; if (node) { try { node.stop?.(); } catch {} } nodesRef.current.splice(idx, 1); return n; });
  };

  const addTone = (freq) => {
    if (rows.length >= MAX_TONES) { toast.error(`Max ${MAX_TONES} layers`); return; }
    const nr = { id: Date.now(), freq: Number(freq), gain: 0.15, pan: 0 };
    setRows(p => { const n = [...p, nr]; if (nodesRef.current.length) nodesRef.current.push(playTone({ freq: nr.freq, gain: nr.gain, pan: nr.pan })); return n; });
    toast.success(`Added ${freq} Hz`);
  };

  const addRifeSet = (freqs = []) => {
    let added = 0; const nrs = [];
    for (const f of freqs) { if (added + rows.length >= MAX_TONES) break; added++; nrs.push({ id: Date.now() + Math.random(), freq: Number(f), gain: 0.12, pan: 0 }); }
    if (!added) { toast.error("No room — clear layers first"); return; }
    setRows(p => { const n = [...p, ...nrs]; if (nodesRef.current.length) nrs.forEach(r => nodesRef.current.push(playTone({ freq: r.freq, gain: r.gain, pan: r.pan }))); return n; });
    toast.success(`Added ${added} frequencies`);
  };

  // Replace all current tones with a set of frequencies
  const replaceAllWith = (freqs = [], label = "Archive") => {
    stop();
    const nrs = freqs.slice(0, MAX_TONES).map((f, i) => ({
      id: Date.now() + i,
      freq: Number(f),
      gain: Math.min(0.15, 0.6 / Math.max(freqs.length, 1)),
      pan: 0,
    }));
    setRows(nrs);
    // Auto-play
    nodesRef.current = nrs.map(r => playTone({ freq: r.freq, gain: r.gain, pan: r.pan }));
    toast.success(`Loaded ${nrs.length} frequencies from ${label}`);
  };

  // ─── RESONATE: combo of tone layers + binaural envelope ───
  // Plays the frequencies as layered tones AND a binaural beat at the selected brainwave band.
  // Carrier is the chosen comboCarrier (default 432Hz), beat is the band's Hz.
  const resonate = (freqs = [], label = "Archive") => {
    if (!freqs.length) return;
    stop();
    if (activeCombo === label) { setActiveCombo(null); return; }

    const band = COMBO_BANDS.find(b => b.id === comboBand) || COMBO_BANDS[2];
    const calib = Number(localStorage.getItem("calibrationCents") || 0);

    // Layer the frequencies as tones
    const nrs = freqs.slice(0, MAX_TONES).map((f, i) => ({
      id: Date.now() + i,
      freq: Number(f),
      gain: Math.min(0.10, 0.45 / Math.max(freqs.length, 1)),
      pan: 0,
    }));
    setRows(nrs);
    nodesRef.current = nrs.map(r => playTone({ freq: r.freq, gain: r.gain, pan: r.pan, offsetCents: calib }));

    // Add binaural envelope around the carrier
    binauralRef.current = playBinaural({
      carrierHz: Number(comboCarrier),
      beatHz: band.hz,
      seconds: 600,
      gain: 0.20,
      offsetCents: calib,
    });

    // Sync the binaural controls so user can fine-tune from BINAURAL tab
    setBi(b => ({ ...b, carrier: Number(comboCarrier), beat: band.hz }));
    setActiveCombo(label);
    toast.success(`${label} · ${nrs.length} layers + ${band.name} ${band.hz}Hz`);
  };

  // ─── PROTOCOL: prebuilt combo with custom band + carrier (overrides combo state) ───
  const playProtocol = (proto) => {
    stop();
    if (activeCombo === proto.label) { setActiveCombo(null); return; }
    const calib = Number(localStorage.getItem("calibrationCents") || 0);

    const nrs = proto.layers.slice(0, MAX_TONES).map((layer, i) => ({
      id: Date.now() + i,
      freq: Number(layer.freq),
      gain: layer.gain ?? 0.08,
      pan: 0,
    }));
    setRows(nrs);
    nodesRef.current = nrs.map(r => playTone({ freq: r.freq, gain: r.gain, pan: r.pan, offsetCents: calib }));

    binauralRef.current = playBinaural({
      carrierHz: proto.carrier,
      beatHz: proto.beat,
      seconds: proto.seconds || 600,
      gain: proto.binauralGain ?? 0.22,
      offsetCents: calib,
    });

    setBi(b => ({ ...b, carrier: proto.carrier, beat: proto.beat }));
    setActiveCombo(proto.label);
    toast.success(`${proto.label} · ${nrs.length} layers + ${proto.beat}Hz`);
  };

  const updateBi = (u) => {
    setBi(p => {
      const n = { ...p, ...u };
      if (binauralRef.current) { const { leftOsc, rightOsc, leftGain, rightGain, ctx } = binauralRef.current; const c = Number(n.carrier) || 0, b = Number(n.beat) || 0, now = ctx?.currentTime || 0; leftOsc.frequency.setValueAtTime(c - b / 2 + (Number(n.leftAdjust) || 0), now); rightOsc.frequency.setValueAtTime(c + b / 2 + (Number(n.rightAdjust) || 0), now); leftGain.gain.setValueAtTime(Number(n.gain) || 0, now); rightGain.gain.setValueAtTime(Number(n.gain) || 0, now); }
      return n;
    });
  };

  const applyPreset = (p) => { updateBi({ carrier: p.carrier_hz, beat: p.beat?.beat_hz ?? bi.beat }); toast.success(`Loaded ${p.label}`); };
  const start = () => {
    try {
      nodesRef.current = [];
      rows.forEach(r => {
        // Apply solo/mute: when a layer is soloed, only it plays. Muted layers always silent.
        const muted = !!layerMuted[r.id];
        const effectiveGain = (soloLayer && soloLayer !== r.id) || muted ? 0 : Number(r.gain);
        nodesRef.current.push(playTone({ freq: Number(r.freq), gain: effectiveGain, pan: Number(r.pan) }));
      });
    } catch { toast.error("Audio error"); }
  };
  const stop = () => { stopAll(); nodesRef.current = []; binauralRef.current = null; setActiveCombo(null); };
  const startBi = () => { try { binauralRef.current = playBinaural({ carrierHz: Number(bi.carrier), beatHz: Number(bi.beat), seconds: Math.max(5, Number(bi.minutes) * 60), gain: Number(bi.gain), offsetCents: Number(localStorage.getItem("calibrationCents") || 0), leftAdjust: Number(bi.leftAdjust), rightAdjust: Number(bi.rightAdjust) }); } catch { toast.error("Audio error"); } };

  const fPresets = presets.filter(p => (p.label || "").toLowerCase().includes(q.toLowerCase()));
  const fRife = rife.filter(r => (r.condition || "").toLowerCase().includes(q.toLowerCase()));

  // Dial values — primary tone frequency (Layer 1)
  const dialHz = Number(rows[0]?.freq || 432);
  const dialMin = 1, dialMax = 20000;
  const dialNorm = Math.max(0, Math.min(1, (dialHz - dialMin) / (dialMax - dialMin)));
  const dialAngle = -135 + dialNorm * 270;

  const onDialChange = (val) => {
    if (rows.length) update(0, 'freq', val);
  };

  return (
    <Card className="sg-frame border border-[#444]/50 bg-gradient-to-b from-[#0a0a0c] to-black shadow-[0_0_30px_rgba(192,192,192,0.04)] overflow-hidden" data-testid="tone-gen-card">
      {/* ─── GODMODE Branded Header Strip ─── */}
      <div className="grid grid-cols-[auto_auto_1fr_auto_auto] items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#0f0007] via-[#180005] to-[#0f0007] border-b border-[#FF0000]/30" data-testid="godmode-header-strip">
        {/* LEFT: GODMODE LLC */}
        <div className="flex items-center gap-2 border border-[#FF0000]/30 bg-[#0a0000] px-2 py-1">
          <img src="https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/m4j4rtmm_MIRRORS_1024x1024.jpg"
            alt="GODMODE" className="h-7 w-7 rounded object-cover"
            style={{ filter: 'drop-shadow(0 0 4px rgba(255,0,0,0.6))' }}
            referrerPolicy="no-referrer" />
          <div className="leading-tight">
            <div className="text-[10px] font-bold text-[#FF0000] tracking-widest">GODMODE LLC</div>
            <div className="text-[7px] text-[#FF0000]/50 tracking-[0.2em]">GODMODE SOUND</div>
          </div>
        </div>

        {/* MIDDLE-LEFT: MIRRORS // 96 + subtitle */}
        <div className="hidden sm:block border border-[#FF0000]/20 bg-[#0a0000] px-2 py-1 leading-tight">
          <div className="text-[9px] font-bold text-[#FF0000] tracking-[0.2em]">MIRRORS // 96 MIRRORS</div>
          <div className="text-[7px] text-[#FF0000]/60 tracking-widest">FREQUENCY STACK GENERATOR</div>
          <div className="text-[7px] text-[#FF0000]/60 tracking-widest">BINAURAL BEAT GENERATOR</div>
        </div>

        {/* CENTER: GODMODE — FREQUENCY IS CODE */}
        <div className="text-center px-1">
          <div className="text-base sm:text-lg font-black text-[#FF0000] tracking-[0.25em] leading-none"
            style={{ textShadow: '0 0 10px rgba(255,0,0,0.5), 0 0 20px rgba(255,0,0,0.3)' }}>
            GODMODE
          </div>
          <div className="text-[7px] sm:text-[8px] text-[#FF0000]/50 tracking-[0.4em] mt-0.5">FREQUENCY IS CODE</div>
        </div>

        {/* MIDDLE-RIGHT: 432Hz Master Tuning + live EQ */}
        <div className="hidden md:flex items-center gap-2 border border-[#FF0000]/20 bg-[#0a0000] px-2 py-1">
          <div className="leading-tight">
            <div className="text-sm font-bold text-[#FF0000] tracking-tight">432Hz</div>
            <div className="text-[6px] text-[#FF0000]/60 tracking-widest">MASTER TUNING</div>
          </div>
          <div className="flex items-end gap-[2px] h-7" data-testid="header-eq-bars">
            {[3,5,8,12,16,11,7,4,9,13,6,3].map((bh, i) => (
              <div key={i}
                className="w-[2px] bg-gradient-to-t from-[#FF0000] to-[#FF0000]/30 animate-pulse"
                style={{ height: `${bh * 2}px`, animationDelay: `${i * 80}ms`, animationDuration: '900ms' }} />
            ))}
          </div>
        </div>

        {/* RIGHT: MIRRORS ID card */}
        <img src="https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/up1wyv92_MIRRORS%20ID.jpeg"
          alt="MIRRORS ID" className="h-10 w-auto rounded"
          style={{ filter: 'drop-shadow(0 0 6px rgba(255,0,0,0.4))' }}
          referrerPolicy="no-referrer" />
      </div>

      {/* ─── Quick PLAY / STOP bar ─── */}
      <div className="flex items-center justify-end gap-1.5 px-4 py-1.5 bg-[#08080a] border-b border-[#222]/60">
        <Button size="sm" onClick={start} className="bg-[#FF0000] text-black hover:bg-[#FF3333] font-mono text-xs px-3 h-7" data-testid="fl-play-btn">
          <Play className="w-3 h-3 mr-1" /> PLAY
        </Button>
        <Button size="sm" onClick={stop} variant="outline" className="font-mono text-[#FF0000] border-[#FF0000]/40 text-xs px-3 h-7 hover:bg-[#1a0505]" data-testid="fl-stop-btn">
          <Square className="w-3 h-3 mr-1" /> STOP
        </Button>
      </div>

      <CardContent className="p-0 font-mono text-[#C0C0C0] relative">
        {/* Scanline overlay */}
        <div className="absolute inset-0 pointer-events-none bg-[repeating-linear-gradient(0deg,rgba(192,192,192,0.015),rgba(192,192,192,0.015)_1px,transparent_1px,transparent_3px)] z-10" />

        {/* ─── T2 Liquid Metal Dial ─── */}
        <div className="relative z-20 px-4 pt-4 pb-3">
          <div className="grid gap-3 rounded border border-[#333]/40 bg-gradient-to-br from-[#0c0c0e] to-[#060608] p-3 md:grid-cols-[130px_1fr]" data-testid="fl-dial-section">
            {/* Dial */}
            <div className="flex flex-col items-center gap-1.5" data-testid="fl-dial">
              <div className="relative h-[100px] w-[100px] rounded-full border-2 border-[#555]/50 bg-gradient-to-br from-[#1a1a1e] via-[#0d0d10] to-[#050507] shadow-[0_0_20px_rgba(192,192,192,0.1)]">
                <div className="absolute inset-2 rounded-full border border-[#444]/25 bg-gradient-to-br from-[#141416] to-[#08080a]" />
                <div className="absolute inset-4 rounded-full border border-[#555]/15" />
                <div
                  className="dial-needle absolute left-1/2 top-1/2 h-1.5 w-[42px] origin-left rounded-full bg-gradient-to-r from-[#E8E8EC] via-[#C0C0C0] to-[#FF0000]"
                  style={{ transform: `translateY(-50%) rotate(${dialAngle}deg)` }}
                />
                <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4D4D8] shadow-[0_0_12px_rgba(212,212,216,0.7)]" />
                <div className="absolute bottom-1 w-full text-center text-[0.4rem] uppercase tracking-[0.4em] text-[#555]">T-1000</div>
              </div>
              <div className="rounded-full border border-[#444]/40 bg-gradient-to-r from-[#111114] via-[#1a1a1e] to-[#111114] px-3 py-0.5 text-[0.7rem] text-[#C0C0C0] shadow-[inset_0_0_8px_rgba(192,192,192,0.06)]" data-testid="fl-dial-readout">
                {dialHz.toFixed(1)} Hz
              </div>
            </div>
            {/* Slider + labels */}
            <div className="space-y-1.5 flex flex-col justify-center">
              <div className="flex items-center justify-between text-[10px] text-[#666]">
                <span>{dialMin} Hz</span>
                <span className="text-[#C0C0C0] font-semibold">Layer 1: {dialHz} Hz</span>
                <span>{dialMax} Hz</span>
              </div>
              <div className="relative h-2.5 rounded-full border border-[#333]/50 bg-[#0a0a0c] overflow-hidden">
                <div className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#333]/50 via-[#555]/40 to-[#FF0000]/35" style={{ width: `${dialNorm * 100}%` }} />
              </div>
              <input type="range" min={dialMin} max={dialMax} step={1} value={dialHz} onChange={e => onDialChange(Number(e.target.value))} className="w-full accent-[#FF0000]" data-testid="fl-dial-slider" />
              <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-[#444]">
                <span>Sub</span><span>Low</span><span>Mid</span><span>High</span><span>Ultra</span>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Unified Panel: Archive · Tones · Binaural all visible ─── */}
        <div className="p-4 relative z-20 space-y-6">
          {/* ═══ SECTION 1 · ARCHIVE ═══ */}
          <section className="space-y-4" data-testid="fl-archive-panel">
            <div className="flex items-center gap-2 border-b border-[#FF0000]/15 pb-1.5">
              <div className="w-1.5 h-3 bg-[#FF0000]" />
              <h3 className="text-[10px] font-mono tracking-[0.3em] text-[#FF0000]">ARCHIVE</h3>
              <span className="text-[8px] font-mono text-[#FF0000]/30 ml-auto">Solfeggio · Protocols · Presets</span>
            </div>
              {/* ── Resonate combo bar (Tone Layers + Binaural Envelope) ── */}
              <div className="border border-[#FF0000]/25 bg-gradient-to-r from-[#0a0a0c] via-[#120505] to-[#0a0a0c] p-2.5 space-y-2" data-testid="resonate-combo-bar">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[8px] tracking-[0.2em] text-[#FF0000]/50 uppercase font-mono">Resonate</span>
                    <span className="text-[7px] text-[#FF0000]/30 font-mono">tones + binaural</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[7px] text-[#FF0000]/40 font-mono mr-1">CARRIER</span>
                    <Input
                      type="number"
                      step="1"
                      value={comboCarrier}
                      onChange={e => setComboCarrier(Number(e.target.value) || 432)}
                      className="w-14 h-6 bg-black border-[#FF0000]/30 text-[#FF0000] text-[10px] font-mono px-1.5"
                      data-testid="combo-carrier-input"
                    />
                    <span className="text-[7px] text-[#FF0000]/30 font-mono">Hz</span>
                  </div>
                </div>
                <div className="flex items-center gap-1" data-testid="combo-band-picker">
                  {COMBO_BANDS.map(b => (
                    <button
                      key={b.id}
                      onClick={() => setComboBand(b.id)}
                      title={`${b.name} · ${b.hz}Hz · ${b.desc}`}
                      className={`flex-1 px-1.5 py-1 border transition-all active:scale-95 ${
                        comboBand === b.id
                          ? "border-[#FF0000] bg-[#FF0000]/15 text-[#FF0000] shadow-[0_0_8px_rgba(255,0,0,0.2)]"
                          : "border-[#333]/60 text-[#888] hover:border-[#FF0000]/40 hover:text-[#FF0000]"
                      }`}
                      data-testid={`combo-band-${b.id}`}
                    >
                      <div className="text-[11px] font-bold leading-none">{b.label}</div>
                      <div className="text-[7px] mt-0.5 leading-none opacity-70">{b.hz}Hz</div>
                    </button>
                  ))}
                </div>

                {/* ── Prebuilt protocols ── */}
                <div className="pt-1.5 border-t border-[#FF0000]/10" data-testid="protocols-row">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[8px] tracking-[0.2em] text-[#FF0000]/50 uppercase font-mono">Protocols</span>
                    <span className="text-[7px] text-[#FF0000]/30 font-mono">calibrated recipes</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {PROTOCOLS.map(p => {
                      const active = activeCombo === p.label;
                      return (
                        <div
                          key={p.id}
                          className={`flex-1 min-w-[180px] flex items-center justify-between gap-2 border px-2 py-1.5 transition-all ${
                            active
                              ? "border-[#FF0000] bg-[#FF0000]/10 shadow-[0_0_10px_rgba(255,0,0,0.2)]"
                              : "border-[#FF0000]/20 bg-black/50 hover:border-[#FF0000]/40"
                          }`}
                          data-testid={`protocol-${p.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-bold text-[#FF0000] truncate">{p.label}</div>
                            <div className="text-[7px] text-[#FF0000]/50 truncate">{p.description}</div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              size="xs"
                              onClick={() => playProtocol(p)}
                              className={`h-6 px-2 text-[9px] font-mono ${
                                active
                                  ? "bg-[#FF0000] text-black animate-pulse"
                                  : "bg-[#FF0000]/80 text-black hover:bg-[#FF0000]"
                              }`}
                              data-testid={`protocol-play-${p.id}`}
                            >
                              {active ? "■" : "▶"}
                            </Button>
                            {p.download && (
                              <a
                                href={p.download}
                                download
                                className="h-6 px-2 inline-flex items-center text-[9px] font-mono border border-[#FF0000]/40 text-[#FF0000]/70 hover:text-[#FF0000] hover:bg-[#FF0000]/10"
                                data-testid={`protocol-download-${p.id}`}
                                title="Download WAV"
                              >
                                WAV
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ── Quick Bulk Actions ── */}
              <div className="flex gap-1.5 flex-wrap" data-testid="fl-archive-bulk">
                <Button
                  size="sm"
                  onClick={() => resonate(QUICK_FREQS.map(f => f.hz), "Solfeggio")}
                  className={`text-[9px] h-6 px-2.5 font-mono tracking-wider ${
                    activeCombo === "Solfeggio"
                      ? "bg-[#FF0000] text-black animate-pulse"
                      : "bg-[#FF0000]/15 text-[#FF0000] border border-[#FF0000]/40 hover:bg-[#FF0000]/25"
                  }`}
                  data-testid="fl-resonate-solfeggio"
                >
                  {activeCombo === "Solfeggio" ? "RESONATING…" : "RESONATE SOLFEGGIO"}
                </Button>
                {fRife.length > 0 && (
                  <Button
                    size="sm"
                    onClick={() => {
                      const allFreqs = [];
                      fRife.forEach(r => r.frequencies.forEach(f => { if (!allFreqs.includes(f)) allFreqs.push(f); }));
                      resonate(allFreqs, "Frequency Archive");
                    }}
                    className={`text-[9px] h-6 px-2.5 font-mono tracking-wider ${
                      activeCombo === "Frequency Archive"
                        ? "bg-[#FF0000] text-black animate-pulse"
                        : "bg-[#FF0000]/15 text-[#FF0000] border border-[#FF0000]/40 hover:bg-[#FF0000]/25"
                    }`}
                    data-testid="fl-resonate-all-rife"
                  >
                    {activeCombo === "Frequency Archive" ? "RESONATING…" : "RESONATE ARCHIVE"}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { stop(); setRows([{ id: 1, freq: 432, gain: 0.2, pan: 0 }]); toast.success("Cleared"); }}
                  className="text-[9px] h-6 px-2.5 border-[#FF0000]/30 text-[#FF0000] hover:bg-[#FF0000]/10 font-mono tracking-wider"
                  data-testid="fl-clear-all"
                >
                  CLEAR
                </Button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] tracking-[0.25em] text-[#666] uppercase">Quick Frequencies</div>
                  <Button
                    size="xs"
                    onClick={() => resonate(QUICK_FREQS.map(f => f.hz), "Quick Solfeggio")}
                    className={`text-[8px] h-5 px-2 ${
                      activeCombo === "Quick Solfeggio"
                        ? "bg-[#FF0000] text-black animate-pulse"
                        : "bg-[#FF0000]/80 text-black hover:bg-[#FF0000]"
                    }`}
                    data-testid="fl-resonate-quick"
                  >
                    {activeCombo === "Quick Solfeggio" ? "■ STOP" : "▶ RESONATE"}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="fl-quick-freqs">
                  {QUICK_FREQS.map(f => (
                    <button key={f.hz} onClick={() => addTone(f.hz)}
                      className="group px-2.5 py-1.5 border border-[#FF0000]/25 hover:border-[#FF0000]/60 hover:bg-[#FF0000]/10 transition-all"
                      data-testid={`fl-quick-${f.hz}`}>
                      <div className="text-xs font-bold text-[#FF0000]">{f.label}</div>
                      <div className="text-[7px] text-[#666] group-hover:text-[#888]">{f.tag}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#555]" />
                <Input placeholder="Search conditions, protocols..." value={q} onChange={e => setQ(e.target.value)}
                  className="pl-8 bg-black text-[#C0C0C0] border-[#333] text-xs font-mono placeholder:text-[#444] h-8" data-testid="fl-search" />
              </div>
              <ScrollArea className="h-[260px] sm:h-[320px]">
                <div className="space-y-3 pr-2">
                  {fRife.length > 0 && (
                    <div>
                      <div className="text-[9px] tracking-[0.25em] text-[#666] uppercase mb-2">Frequency Protocols</div>
                      <div className="space-y-2">
                        {fRife.map((r, idx) => {
                          const comboLabel = `Rife · ${r.condition}`;
                          const isActive = activeCombo === comboLabel;
                          return (
                          <div key={`rife-${r.condition}-${idx}`} className="border border-[#333]/50 p-2.5 bg-[#0a0a0c]">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-xs font-medium text-[#C0C0C0]">{r.condition}</span>
                              <div className="flex gap-1">
                                <Button size="xs" onClick={() => replaceAllWith(r.frequencies, r.condition)} className="bg-transparent border border-[#FF0000]/40 text-[#FF0000] text-[8px] h-5 px-1.5 hover:bg-[#FF0000]/10" data-testid={`fl-rife-load-${idx}`}>LOAD</Button>
                                <Button
                                  size="xs"
                                  onClick={() => resonate(r.frequencies, comboLabel)}
                                  className={`text-[9px] h-5 px-2 ${
                                    isActive
                                      ? "bg-[#FF0000] text-black animate-pulse"
                                      : "bg-[#FF0000]/80 text-black hover:bg-[#FF0000]"
                                  }`}
                                  data-testid={`fl-rife-resonate-${idx}`}
                                >
                                  {isActive ? "■ STOP" : "▶ RESONATE"}
                                </Button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {r.frequencies.slice(0, 16).map((f, i) => (
                                <button key={`${f}-${i}`} onClick={() => addTone(f)} className="px-1.5 py-0.5 text-[9px] border border-[#333]/60 text-[#888] hover:text-[#FF0000] hover:border-[#FF0000]/40 transition-colors" data-testid={`fl-rife-${idx}-${i}`}>{f}</button>
                              ))}
                              {r.frequencies.length > 16 && <span className="text-[9px] text-[#444] self-center">+{r.frequencies.length - 16}</span>}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {fPresets.length > 0 && (
                    <div>
                      <div className="text-[9px] tracking-[0.25em] text-[#666] uppercase mb-2">432Hz Presets &#8594; Binaural</div>
                      <div className="space-y-1.5">
                        {fPresets.map(p => (
                          <div key={p.id} className="flex items-center justify-between border border-[#333]/50 p-2 bg-[#0a0a0c]">
                            <div><div className="text-xs text-[#C0C0C0]">{p.label}</div><div className="text-[9px] text-[#555]">{p.carrier_hz} Hz &middot; Beat {p.beat.beat_hz} Hz</div></div>
                            <div className="flex gap-1">
                              <Button size="xs" onClick={() => addTone(p.carrier_hz)} className="bg-transparent border border-[#FF0000]/40 text-[#FF0000] text-[8px] h-5 px-1.5 hover:bg-[#FF0000]/10" data-testid={`fl-preset-add-${p.id}`}>+ ADD</Button>
                              <Button size="xs" onClick={() => applyPreset(p)} className="bg-[#FF0000]/80 text-black text-[9px] h-5 px-2 hover:bg-[#FF0000]" data-testid={`fl-preset-${p.id}`}>USE</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {fRife.length === 0 && fPresets.length === 0 && <div className="text-xs text-[#444] text-center py-8">No results for &ldquo;{q}&rdquo;</div>}

                  {/* ── GODMODE Moods ── */}
                  {!q && (
                  <div>
                    <button onClick={() => toggleSection("moods")} className="flex items-center justify-between w-full mb-2" data-testid="archive-moods-toggle">
                      <div className="text-[9px] tracking-[0.25em] text-[#666] uppercase">GODMODE Moods</div>
                      <ChevronDown className={`w-3 h-3 text-[#555] transition-transform ${expandedSections.moods ? "rotate-180" : ""}`} />
                    </button>
                    {expandedSections.moods && (
                      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="archive-moods-grid">
                        {Object.keys(MOOD_PRESETS).map(label => (
                          <button key={label} onClick={() => handleMood(label)}
                            className={`px-2 py-1 text-[9px] font-mono border transition-all ${currentMood === label ? "border-[#FF0000] bg-[#FF0000]/15 text-[#FF0000]" : "border-[#333]/60 text-[#888] hover:border-[#FF0000]/40 hover:text-[#FF0000]"}`}
                            data-testid={`archive-mood-${label.replace(/\s/g,'-').toLowerCase()}`}>{label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  )}

                  {/* ── Solfeggio ── */}
                  {!q && (
                  <div>
                    <button onClick={() => toggleSection("solfeggio")} className="flex items-center justify-between w-full mb-2" data-testid="archive-solfeggio-toggle">
                      <div className="text-[9px] tracking-[0.25em] text-[#666] uppercase">Solfeggio Tones</div>
                      <ChevronDown className={`w-3 h-3 text-[#555] transition-transform ${expandedSections.solfeggio ? "rotate-180" : ""}`} />
                    </button>
                    {expandedSections.solfeggio && (
                      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="archive-solfeggio-grid">
                        {SOLFEGGIO_FULL.map(hz => (
                          <button key={hz} onClick={() => addTone(hz)}
                            className="px-2 py-1 text-[9px] font-mono border border-[#333]/60 text-[#888] hover:border-[#FF0000]/40 hover:text-[#FF0000] transition-all"
                            data-testid={`archive-solfeggio-${hz}`}>{hz} Hz</button>
                        ))}
                      </div>
                    )}
                  </div>
                  )}

                  {/* ── Chakras ── */}
                  {!q && (
                  <div>
                    <button onClick={() => toggleSection("chakras")} className="flex items-center justify-between w-full mb-2" data-testid="archive-chakras-toggle">
                      <div className="text-[9px] tracking-[0.25em] text-[#666] uppercase">Chakra Frequencies</div>
                      <ChevronDown className={`w-3 h-3 text-[#555] transition-transform ${expandedSections.chakras ? "rotate-180" : ""}`} />
                    </button>
                    {expandedSections.chakras && (
                      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="archive-chakras-grid">
                        {CHAKRAS.map(c => (
                          <button key={c.name} onClick={() => addTone(c.hz)}
                            className="px-2 py-1 text-[9px] font-mono border border-[#333]/60 text-[#888] hover:border-[#FF0000]/40 hover:text-[#FF0000] transition-all"
                            data-testid={`archive-chakra-${c.name.toLowerCase().replace(/\s/g,'-')}`}>{c.name} {c.hz}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  )}

                  {/* ── USA Stacks ── */}
                  {!q && (
                  <div>
                    <button onClick={() => toggleSection("usa")} className="flex items-center justify-between w-full mb-2" data-testid="archive-usa-toggle">
                      <div className="text-[9px] tracking-[0.25em] text-[#666] uppercase">Frequency Presets</div>
                      <ChevronDown className={`w-3 h-3 text-[#555] transition-transform ${expandedSections.usa ? "rotate-180" : ""}`} />
                    </button>
                    {expandedSections.usa && (
                      <div className="space-y-2 mb-3" data-testid="archive-usa-grid">
                        {FREQ_PRESETS.map(protocol => {
                          const freqs = protocol.frequencies?.length ? protocol.frequencies : getProtocolFrequencies(protocol.keywords);
                          return (
                            <div key={protocol.id} className="border border-[#333]/50 p-2 bg-[#0a0a0c]" data-testid={`archive-usa-${protocol.id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] font-mono text-[#C0C0C0]">{protocol.label}</span>
                                <Button size="xs" variant={activeProtocol === protocol.id ? "default" : "outline"}
                                  className={`text-[8px] h-5 ${activeProtocol === protocol.id ? "bg-[#FF0000] text-black" : "border-[#FF0000]/30 text-[#FF0000]"}`}
                                  onClick={() => playProtocolStack(protocol.id, freqs)} data-testid={`archive-usa-play-${protocol.id}`}>
                                  {activeProtocol === protocol.id ? "Stop" : "Play"}
                                </Button>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {freqs.slice(0, 6).map((f, i) => (
                                  <button key={`${protocol.id}-${f}-${i}`} onClick={() => addTone(f)}
                                    className="px-1.5 py-0.5 text-[8px] font-mono border border-[#333]/60 text-[#888] hover:text-[#FF0000] hover:border-[#FF0000]/40 transition-colors">{f}</button>
                                ))}
                                {freqs.length === 0 && <span className="text-[8px] text-[#444]">No match</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  )}

                  {/* ── Popular Beats ── */}
                  {!q && (
                  <div>
                    <button onClick={() => toggleSection("popular")} className="flex items-center justify-between w-full mb-2" data-testid="archive-popular-toggle">
                      <div className="text-[9px] tracking-[0.25em] text-[#666] uppercase">Popular Beats</div>
                      <ChevronDown className={`w-3 h-3 text-[#555] transition-transform ${expandedSections.popular ? "rotate-180" : ""}`} />
                    </button>
                    {expandedSections.popular && (
                      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="archive-popular-grid">
                        {POPULAR_BEATS.map(hz => (
                          <button key={hz} onClick={() => addTone(hz)}
                            className="px-2 py-1 text-[9px] font-mono border border-[#333]/60 text-[#888] hover:border-[#FF0000]/40 hover:text-[#FF0000] transition-all"
                            data-testid={`archive-popular-${hz}`}>{hz} Hz</button>
                        ))}
                      </div>
                    )}
                  </div>
                  )}

                  {/* ── Party Analogs ── */}
                  {!q && (
                  <div>
                    <button onClick={() => toggleSection("party")} className="flex items-center justify-between w-full mb-2" data-testid="archive-party-toggle">
                      <div className="text-[9px] tracking-[0.25em] text-[#666] uppercase">EEG Presets</div>
                      <ChevronDown className={`w-3 h-3 text-[#555] transition-transform ${expandedSections.party ? "rotate-180" : ""}`} />
                    </button>
                    {expandedSections.party && (
                      <div className="space-y-1.5 mb-3" data-testid="archive-party-grid">
                        {EEG_PRESETS.map(analog => (
                          <div key={analog.id} className="flex items-center justify-between border border-[#333]/50 p-2 bg-[#0a0a0c]" data-testid={`archive-party-${analog.id}`}>
                            <div>
                              <div className="text-[10px] font-mono text-[#C0C0C0]">{analog.name}</div>
                              <div className="text-[8px] text-[#555]">{analog.carrier}Hz / {analog.beat}Hz &middot; {analog.desc}</div>
                            </div>
                            <Button size="xs" variant={activeAnalog === analog.id ? "default" : "outline"}
                              className={`text-[8px] h-5 ${activeAnalog === analog.id ? "bg-[#FF0000] text-black" : "border-[#FF0000]/30 text-[#FF0000]"}`}
                              onClick={() => activeAnalog === analog.id ? stopAnalog() : playAnalog(analog)} data-testid={`archive-party-play-${analog.id}`}>
                              {activeAnalog === analog.id ? "Stop" : "Play"}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </ScrollArea>
          </section>

          {/* ═══ SECTION 2 · TONES (Chakra rows) ═══ */}
          <section className="space-y-2" data-testid="fl-tones-panel">
            <div className="flex items-center gap-2 border-b border-[#FF0000]/15 pb-1.5">
              <div className="w-1.5 h-3 bg-[#FF0000]" />
              <h3 className="text-[10px] font-mono tracking-[0.3em] text-[#FF0000]">STACK BUILDER</h3>
              <span className="text-[8px] font-mono text-[#FF0000]/30">{rows.length} CORE LAYERS</span>
              <span className="text-[8px] font-mono text-[#FF0000]/30 ml-auto">{rows.length}/{MAX_TONES}</span>
            </div>
            {rows.map((r, i) => {
              const hz = Number(r.freq) || 0;
              const chakra = chakraFor(hz);
              const isMuted = !!layerMuted[r.id];
              const isSoloed = soloLayer === r.id;
              const inactive = (soloLayer && !isSoloed) || isMuted;
              return (
                <div key={r.id}
                  className={`grid items-center gap-2 border rounded px-2 py-1.5 transition-all ${inactive ? 'opacity-40 border-[#222]/60 bg-black/40' : 'border-[#222]/80 bg-gradient-to-r from-[#0a0a0c] via-[#10080a] to-[#0a0a0c]'}`}
                  style={{
                    gridTemplateColumns: '28px 24px minmax(0,1.1fr) 70px minmax(0,1.4fr) 70px 36px',
                    boxShadow: inactive ? 'none' : `0 0 12px ${chakra.color}10, inset 0 0 16px ${chakra.color}08`,
                  }}
                  data-testid={`fl-layer-${r.id}`}>
                  {/* Number badge */}
                  <div className="text-base font-bold tracking-wider" style={{ color: chakra.color, textShadow: `0 0 6px ${chakra.color}66` }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  {/* Chakra glyph */}
                  <div className="text-base" style={{ color: chakra.color, textShadow: `0 0 8px ${chakra.color}88` }}>{chakra.glyph}</div>
                  {/* Chakra name + intent */}
                  <div className="min-w-0 leading-tight">
                    <div className="text-[10px] font-bold tracking-wider truncate" style={{ color: chakra.color }}>{chakra.key}</div>
                    <div className="text-[7px] text-[#888]/70 tracking-wider truncate">{chakra.intent}</div>
                  </div>
                  {/* Hz input */}
                  <Input type="number" step="0.1" value={r.freq} onChange={e => update(i, 'freq', e.target.value)}
                    className="bg-black border-[#333] text-[10px] font-mono h-7 px-1.5 text-right"
                    style={{ color: chakra.color }}
                    data-testid={`fl-freq-${r.id}`} />
                  {/* Sparkline (audio profile) */}
                  <Sparkline color={chakra.color} freq={hz} gain={r.gain} />
                  {/* Gain slider with dB label */}
                  <div className="flex flex-col items-stretch gap-0.5">
                    <input type="range" min="0" max="0.5" step="0.01" value={r.gain} onChange={e => update(i, 'gain', e.target.value)}
                      className="w-full h-1 cursor-pointer"
                      style={{ accentColor: chakra.color }}
                      data-testid={`fl-gain-${r.id}`} />
                    <div className="text-[7px] font-mono text-right" style={{ color: chakra.color + 'AA' }}>
                      {(20 * Math.log10(Math.max(0.0001, r.gain * 2))).toFixed(1)} dB
                    </div>
                  </div>
                  {/* S / M / X */}
                  <div className="flex items-center gap-[2px]">
                    <button onClick={() => toggleSolo(r.id)}
                      className={`w-5 h-5 text-[8px] font-bold border transition-colors ${isSoloed ? 'bg-[#FFD700] text-black border-[#FFD700]' : 'bg-black/60 text-[#FFD700]/70 border-[#FFD700]/40 hover:bg-[#FFD700]/10'}`}
                      title="Solo" data-testid={`fl-solo-${r.id}`}>S</button>
                    <button onClick={() => toggleMute(r.id)}
                      className={`w-5 h-5 text-[8px] font-bold border transition-colors ${isMuted ? 'bg-[#888] text-black border-[#888]' : 'bg-black/60 text-[#888]/80 border-[#888]/40 hover:bg-[#888]/10'}`}
                      title="Mute" data-testid={`fl-mute-${r.id}`}>M</button>
                    <button onClick={() => removeRow(i)}
                      className="w-5 h-5 text-[10px] font-bold border bg-black/60 text-[#FF0000]/80 border-[#FF0000]/40 hover:bg-[#FF0000]/15 hover:text-[#FF0000] transition-colors"
                      title="Remove" data-testid={`fl-rm-${r.id}`}>×</button>
                  </div>
                </div>
              );
            })}
            <Button onClick={addRow} variant="outline" size="sm"
              className="w-full font-mono text-xs border-dashed border-[#FF0000]/30 text-[#FF0000]/60 hover:text-[#FF0000] hover:border-[#FF0000]/60 hover:bg-[#FF0000]/5 h-8"
              data-testid="fl-add-layer">
              + ADD FREQUENCY LAYER · DRAG FROM ARCHIVE OR CLICK TO BROWSE
            </Button>
          </section>

          {/* ═══ SECTION 3 · BINAURAL ═══ */}
          <section className="space-y-4" data-testid="fl-binaural-panel">
            <div className="flex items-center gap-2 border-b border-[#FF0000]/15 pb-1.5">
              <div className="w-1.5 h-3 bg-[#FF0000]" />
              <h3 className="text-[10px] font-mono tracking-[0.3em] text-[#FF0000]">BINAURAL ENVELOPE</h3>
              <Button
                size="xs"
                onClick={startBi}
                className="ml-auto h-5 px-2 text-[8px] font-mono bg-[#FF0000]/20 text-[#FF0000] border border-[#FF0000]/40 hover:bg-[#FF0000]/30"
                data-testid="fl-binaural-play"
              >
                <Play className="w-2.5 h-2.5 mr-0.5" /> START BINAURAL
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><div className="text-[9px] text-[#666] mb-1">Carrier Hz</div>
                <Input type="number" step="0.1" value={bi.carrier} onChange={e => updateBi({ carrier: e.target.value })} className="bg-black text-[#C0C0C0] border-[#444] text-xs font-mono h-8" data-testid="fl-bi-carrier" /></div>
              <div><div className="text-[9px] text-[#666] mb-1">Beat Hz</div>
                <Input type="number" step="0.1" value={bi.beat} onChange={e => updateBi({ beat: e.target.value })} className="bg-black text-[#C0C0C0] border-[#444] text-xs font-mono h-8" data-testid="fl-bi-beat" /></div>
              <div><div className="text-[9px] text-[#666] mb-1">Gain</div>
                <Input type="number" step="0.05" value={bi.gain} onChange={e => updateBi({ gain: e.target.value })} className="bg-black text-[#C0C0C0] border-[#444] text-xs font-mono h-8" data-testid="fl-bi-gain" /></div>
            </div>
            <div>
              <div className="text-[9px] text-[#666] mb-2">Brainwave Presets</div>
              <div className="flex flex-wrap gap-1.5">
                {[{ hz: 2, label: "DELTA", sub: "Deep Sleep" },{ hz: 6, label: "THETA", sub: "Meditation" },{ hz: 10, label: "ALPHA", sub: "Calm" },{ hz: 14, label: "BETA", sub: "Focus" },{ hz: 40, label: "GAMMA", sub: "Insight" }].map(b => (
                  <button key={b.hz} onClick={() => updateBi({ beat: b.hz })}
                    className={`px-2.5 py-1.5 border transition-all ${Number(bi.beat) === b.hz ? "border-[#FF0000] bg-[#FF0000]/15 text-[#FF0000]" : "border-[#333]/60 text-[#888] hover:border-[#FF0000]/40 hover:text-[#FF0000]"}`}
                    data-testid={`fl-bp-${b.label.toLowerCase()}`}>
                    <div className="text-[10px] font-bold">{b.label}</div>
                    <div className="text-[7px] opacity-70">{b.hz} Hz &middot; {b.sub}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><div className="text-[9px] text-[#666] mb-1">Left +Hz</div>
                <Input type="number" step="0.1" value={bi.leftAdjust} onChange={e => updateBi({ leftAdjust: e.target.value })} className="bg-black text-[#C0C0C0] border-[#444] text-xs font-mono h-8" data-testid="fl-bi-left" /></div>
              <div><div className="text-[9px] text-[#666] mb-1">Right +Hz</div>
                <Input type="number" step="0.1" value={bi.rightAdjust} onChange={e => updateBi({ rightAdjust: e.target.value })} className="bg-black text-[#C0C0C0] border-[#444] text-xs font-mono h-8" data-testid="fl-bi-right" /></div>
              <div><div className="text-[9px] text-[#666] mb-1">Duration (min)</div>
                <Input type="number" step="1" value={bi.minutes} onChange={e => updateBi({ minutes: e.target.value })} className="bg-black text-[#C0C0C0] border-[#444] text-xs font-mono h-8" data-testid="fl-bi-mins" /></div>
            </div>
          </section>
        </div>
      </CardContent>
    </Card>
  );
}
